import React from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, MapPin, Users as UsersIcon, ArrowRight, Clock } from "lucide-react";
import { statusBadge, vacancyLabel } from "../data/properties";
import { ACCESSIBILITY_TAGS, SUITABILITY_TAGS } from "../data/propertyModel";

const A11Y_LOOKUP = Object.fromEntries(ACCESSIBILITY_TAGS.map((t) => [t.value, t.label]));
const SUIT_LOOKUP = Object.fromEntries(SUITABILITY_TAGS.map((t) => [t.value, t.label]));

function relativeTimeSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.round((Date.now() - then) / (1000 * 60 * 60 * 24)));
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
}

/**
 * Redesigned SIL / SDA listing card per the accommodation portal brief:
 * - Vacancies-first hierarchy (e.g. "2 vacancies available")
 * - 3–4 clean tags + "+N features" overflow chip
 * - Vacancy reviewed timestamp
 * - View Home + Enquire dual actions
 * - No $/week display (moved to detail page only when data is verified).
 */
export default function PropertyListingCard({ property }) {
  const p = property;
  if (!p) return null;
  const badge = statusBadge(p);
  const vacancy = vacancyLabel(p);
  const heroImg = p.hero_image || (p.images && p.images[0] && p.images[0].src);
  const heroAlt = (p.images && p.images[0] && p.images[0].alt) || p.name;

  // Prefer accessibility tags; add forensic / complex suitability tags where present.
  const accessibleTags = (p.accessibility_features || []).slice(0, 3);
  const suitabilityHighlight = (p.suitability_tags || []).find((t) => ["forensic", "complex-health", "hospital-discharge", "high-intensity", "behaviours-of-concern"].includes(t));
  const displayTags = suitabilityHighlight
    ? [...accessibleTags.slice(0, 3), { value: suitabilityHighlight, isSuit: true }]
    : accessibleTags;
  const remainingTagCount = Math.max(0, (p.accessibility_features?.length || 0) - accessibleTags.length);

  // Service classification badge
  const serviceBadge = p.sda_category ? "SDA" : "SIL";

  const isDemo = p.recordVerificationStatus === "demonstration";
  const reviewedAgo = relativeTimeSince(p.vacancy_last_updated);

  const tone = {
    green: "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]",
    purple: "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]",
    amber: "!bg-[#FDF3E1] !text-[#8C5A0D] !border-[#F1D9A6]",
    neutral: "!bg-white !text-[color:var(--alpha-ink)] !border-white",
  }[badge.tone] || "";

  return (
    <article
      className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all bg-white border border-[color:var(--alpha-line)] flex flex-col"
      data-testid={`listing-card-${p.slug}`}
    >
      {/* Stretched link — the whole card navigates, but Enquire button escapes. */}
      <Link
        to={p.href || `/homes/${p.slug}`}
        aria-label={`View ${p.name}`}
        className="absolute inset-0 z-[1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--alpha-purple-700)]"
        data-testid={`listing-view-${p.slug}`}
      />

      {/* IMAGE */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={heroImg}
          alt={heroAlt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2F1A5C]/55 via-transparent to-transparent" />

        {/* Service + status badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="alpha-chip !py-1 !px-2.5 !text-[10.5px] font-bold uppercase tracking-[0.18em] !bg-white !text-[color:var(--alpha-purple-800)] !border-white">
            {serviceBadge}
          </span>
          <span className={`alpha-chip !py-1 !px-2.5 !text-[10.5px] font-semibold uppercase tracking-[0.18em] ${tone}`}>
            {badge.label}
          </span>
        </div>

        {/* Demo watermark — only shown for demonstration records in preview */}
        {isDemo && (
          <span className="absolute top-3 right-3 alpha-chip !py-1 !px-2 !text-[9.5px] font-semibold uppercase tracking-[0.18em] !bg-[#111]/70 !text-white !border-transparent" data-testid={`listing-demo-badge-${p.slug}`}>
            Demo listing
          </span>
        )}

        {/* Location pill */}
        <div className="absolute bottom-3 left-3 text-white/95 text-xs flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> {p.suburb}, {p.state}
        </div>
      </div>

      {/* BODY */}
      <div className="p-5 flex-1 flex flex-col">
        {/* Vacancy headline — leads the copy (Part 10) */}
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[color:var(--alpha-green-700)]" data-testid={`listing-vacancy-${p.slug}`}>
          {vacancy}
        </div>

        <h3 className="mt-1 font-display font-semibold text-lg leading-snug group-hover:text-[color:var(--alpha-purple-700)] transition-colors">
          {p.name}
        </h3>

        {/* Quick facts row */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[color:var(--alpha-mute)]">
          <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> {p.participant_bedrooms || p.total_bedrooms || 0} bed{(p.participant_bedrooms || p.total_bedrooms || 0) === 1 ? "" : "s"}</span>
          <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" /> {p.bathrooms || 0} bath</span>
          {p.support_ratios?.length > 0 && (
            <span className="flex items-center gap-1.5"><UsersIcon className="w-4 h-4" /> {p.support_ratios.join(" / ")}</span>
          )}
        </div>

        {/* Tags — 3-4 max, then "+N features" */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {displayTags.map((t) => {
            const key = t.value || t;
            const isSuit = t.isSuit;
            const label = isSuit ? SUIT_LOOKUP[key] : A11Y_LOOKUP[key] || key;
            return (
              <span
                key={key}
                className={`alpha-chip !text-[11px] !py-1 !px-2 ${isSuit ? "!bg-[color:var(--alpha-purple-50)] !text-[color:var(--alpha-purple-800)] !border-[color:var(--alpha-purple-100)]" : "alpha-chip-neutral"}`}
              >
                {label}
              </span>
            );
          })}
          {remainingTagCount > 0 && (
            <span className="alpha-chip alpha-chip-neutral !text-[11px] !py-1 !px-2" data-testid={`listing-more-tags-${p.slug}`}>
              +{remainingTagCount} features
            </span>
          )}
        </div>

        {/* Footer — reviewed date + actions */}
        <div className="mt-auto pt-4 flex items-end justify-between border-t border-[color:var(--alpha-line)]/70 mt-4 gap-3">
          {reviewedAgo ? (
            <div className="text-[11px] text-[color:var(--alpha-mute)] inline-flex items-center gap-1" data-testid={`listing-reviewed-${p.slug}`}>
              <Clock className="w-3 h-3" /> Reviewed {reviewedAgo}
            </div>
          ) : <span />}
          <Link
            to={`${p.href || `/homes/${p.slug}`}/enquire`}
            onClick={(e) => e.stopPropagation()}
            className="relative z-[2] inline-flex items-center gap-1 text-sm font-semibold text-white bg-[color:var(--alpha-purple-700)] hover:bg-[color:var(--alpha-purple-800)] rounded-full px-3 py-1.5 transition-colors"
            data-testid={`listing-enquire-${p.slug}`}
          >
            Enquire <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
